import type {
  OperationsNode,
  OperationsScheduleNode,
} from '../types/operations.types'
import { isScheduleNode } from './nodeTypes'

export type CompetitionType = 'Season' | 'Conference' | 'Division'

export interface ResolvedType {
  /** Preselected value for the Type field. */
  type: CompetitionType
  /** Whether the user may change it. */
  locked: boolean
}

const titleCase = (type: string): CompetitionType =>
  (type.charAt(0).toUpperCase() + type.slice(1)) as CompetitionType

/**
 * What Type a NEW child of `parent` should be, and whether it is fixed.
 *
 * Siblings must all be the same kind — a season cannot hold a conference and a
 * division side by side — so once a parent has one child schedule, every later
 * child is forced to match it. Only an empty parent leaves a real choice, and
 * only where the hierarchy itself allows one:
 *
 * - A **conference** can only ever hold divisions, so it is always locked.
 * - An empty **season** may take either, so the user picks (defaulting to
 *   Division, the common case).
 */
export const resolveNewChildType = (
  parent: OperationsScheduleNode
): ResolvedType => {
  // A conference's children are divisions by hierarchy rule, empty or not.
  if (parent.type === 'conference') {
    return { type: 'Division', locked: true }
  }

  const firstChildSchedule = parent.children.find(isScheduleNode)
  if (firstChildSchedule) {
    // Match the existing siblings — mixing kinds under one parent is invalid.
    return { type: titleCase(firstChildSchedule.type), locked: true }
  }

  return { type: 'Division', locked: false }
}

/**
 * Type of the node being EDITED. Always locked: changing a division into a
 * conference (or back) would re-shape the tree around it — its own children and
 * its siblings would no longer be valid at that level.
 */
export const resolveEditType = (node: OperationsNode): ResolvedType => ({
  type: titleCase(node.type),
  locked: true,
})
